/**
 * Multi-unit Liquid Cooling CDU Summary display (#1154 / #996 / Story 4.2) —
 * descriptor-engine-driven rows via a thin shim so Summary consumers'
 * `getPerLiquidCoolingCduSummaryDefinitions` / `getLiquidCoolingSharedSummary
 * Definitions` imports are undisturbed.
 */

import {
  LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS,
  LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS,
  LIQUID_COOLING_CDU_PER_INSTANCE_EDITOR_FIELD_KEYS,
} from '../request/fields/requestFieldRegistry';
import {
  getLiquidCoolingDescriptorSharedSummaryDefinitions,
  getLiquidCoolingDescriptorSummaryDefinitions,
  getLiquidCoolingDescriptorUnitTitle,
  liquidCoolingDescriptorUnitRowValue,
} from '../request/sections/liquidCoolingDescriptorSummary';
import { countSubsectionFields } from '../utils/subsectionHeaderMetrics';

import type { SummaryCardRow } from './summaryCardSourceTypes';
import type { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';

export {
  LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS as LIQUID_COOLING_CDU_SHARED_REQUEST_FIELD_KEYS,
  LIQUID_COOLING_CDU_PER_INSTANCE_EDITOR_FIELD_KEYS as LIQUID_COOLING_CDU_PER_INSTANCE_FIELD_KEYS,
};

/**
 * Per-unit Summary rows for one CDU — engine-driven source (Story 4.2).
 * Byte-identical to the pre-engine bespoke row set (parity oracle
 * `liquid-cooling-summary-rows.json`).
 */
export function getPerLiquidCoolingCduSummaryDefinitions(): SummaryCardRow[] {
  return getLiquidCoolingDescriptorSummaryDefinitions();
}

/**
 * Shared (`lcLiquidToChipDesired`) Summary rows — engine-driven source
 * (Story 4.2). Rendered once by the Summary regardless of unit type.
 */
export function getLiquidCoolingSharedSummaryDefinitions(): SummaryCardRow[] {
  return getLiquidCoolingDescriptorSharedSummaryDefinitions();
}

export function resolveSummaryLiquidCoolingCduConfigurations(input: {
  sessionUnits?: LiquidCoolingCduConfiguration[] | null;
  draftUnits?: LiquidCoolingCduConfiguration[] | null;
  persistedUnits?: LiquidCoolingCduConfiguration[] | null;
  /** #997 C3: true when edited this session — an empty edited array is authoritative (no delete-all resurrection). */
  edited?: boolean;
}): LiquidCoolingCduConfiguration[] {
  const { sessionUnits, draftUnits, persistedUnits, edited } = input;
  if (sessionUnits && sessionUnits.length > 0) {
    return sessionUnits;
  }
  if (edited) {
    return draftUnits ?? [];
  }
  if (draftUnits && draftUnits.length > 0) {
    return draftUnits;
  }
  if (persistedUnits && persistedUnits.length > 0) {
    return persistedUnits;
  }
  return [];
}

export function liquidCoolingCduSummaryUnitRowValue(
  unit: LiquidCoolingCduConfiguration,
  dataField: string,
): string | number | boolean | null | undefined {
  return liquidCoolingDescriptorUnitRowValue(unit, dataField);
}

/**
 * Per-unit Summary card title — descriptor-driven (mirrors the Racks shim
 * `getRackSummaryUnitTitle`). Story 4.2 golden parity oracle: the offline /
 * Summary golden test pins the production title output, not a local helper.
 */
export function getLiquidCoolingCduSummaryUnitTitle(
  unit: LiquidCoolingCduConfiguration,
  index: number,
): string {
  return getLiquidCoolingDescriptorUnitTitle(unit, index);
}

const LC_CDU_PER_INSTANCE_COUNT_FIELDS =
  LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS.filter(
    field =>
      !LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS.includes(field.key as never),
  );

export function countFilledLiquidCoolingCduSummaryFields(
  unit: LiquidCoolingCduConfiguration,
  rows?: readonly SummaryCardRow[],
): number {
  const fieldKeys = new Set(
    (rows ?? getPerLiquidCoolingCduSummaryDefinitions()).map(
      row => row.dataField,
    ),
  );
  const fields = LC_CDU_PER_INSTANCE_COUNT_FIELDS.filter(field =>
    fieldKeys.has(field.key),
  );
  return countSubsectionFields(
    fields,
    unit as unknown as Record<string, unknown>,
  ).filled;
}

export function countFilledLiquidCoolingCduSummaryFieldsForUnits(
  units: readonly LiquidCoolingCduConfiguration[],
  rows: readonly SummaryCardRow[],
): number {
  return units.reduce(
    (sum, unit) => countFilledLiquidCoolingCduSummaryFields(unit, rows) + sum,
    0,
  );
}
